import {
  Button,
  ButtonGroup,
  Center,
  CircularProgress,
  ComposedCard,
  Stack,
  WrapChildrenWithText,
  runIfFn,
} from '@eigtech/ui-shared-dave'
import { getLog } from '@eigtech/ui-shared-logging'
import { QueryErrorResetBoundary } from '@eigtech/ui-shared-api'
import React, { ReactNode, Suspense, SuspenseProps } from 'react'

const log = getLog()

export type SuspensefulQueryProps = SuspenseProps &
  Partial<Omit<QueryErrorBoundaryProps, 'children'>>

export function SuspensefulQuery({ children, fallback, ...boundaryProps }: SuspensefulQueryProps) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <QueryErrorBoundary onReset={reset} {...boundaryProps}>
          <Suspense
            fallback={
              fallback ?? (
                <Center>
                  <CircularProgress isIndeterminate size={100} />
                </Center>
              )
            }
          >
            {children}
          </Suspense>
        </QueryErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  )
}

export type QueryErrorBoundaryProps = {
  children: React.ReactNode
  disableRetry?: boolean
  errorComponent?: ReactNode | ((props: { error: unknown }) => ReactNode)
  errorMessage?: ReactNode | ((props: { error: unknown }) => ReactNode)
  errorLink?: ReactNode
  onReset?: () => void
}

type QueryErrorBoundaryState = { hasError: boolean; error: unknown }

export class QueryErrorBoundary extends React.Component<
  QueryErrorBoundaryProps,
  QueryErrorBoundaryState
> {
  constructor(props: QueryErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: undefined }
  }

  static getDerivedStateFromError(error: unknown) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error }
  }

  componentDidCatch(error: unknown, errorInfo: unknown) {
    // You can also log the error to an error reporting service
    log.error('Page Error Boundary', { error, errorInfo })
  }

  render() {
    if (this.state.hasError) {
      return this.props.errorComponent ? (
        runIfFn(this.props.errorComponent, { error: this.state.error })
      ) : (
        <ComposedCard heading="Whoops!">
          <Stack spacing="4">
            <WrapChildrenWithText>
              {this.props.errorMessage
                ? runIfFn(this.props.errorMessage, { error: this.state.error })
                : 'Something unexpected happened and caused an error. Please try again or contact the EIG Tech team for help.'}
            </WrapChildrenWithText>

            <Center>
              <ButtonGroup>
                {!this.props.disableRetry && (
                  <Button onClick={() => window.location.reload()}>Reload</Button>
                )}
                {this.props.errorLink ?? (
                  <Button as="a" href="/">
                    Go Home
                  </Button>
                )}
              </ButtonGroup>
            </Center>
          </Stack>
        </ComposedCard>
      )
    }

    return this.props.children
  }
}
